// Minimal local i18n (round-3 feedback item 7d, 2026-08-31) — NOT a framework, and NOT app-wide:
// originally scoped to HelpOverlay.vue's content only (Home.vue's one-shot Start hint, 2026-08-31,
// is the one other consumer — everything else in the kiosk stays English, matching existing
// copy). Round-31 (2026-08-31) added a third consumer, the "Fast mode" setting + its LiveMatch
// confirm/error copy — the user asked specifically for es-ES-first labelling there, unlike the
// rest of SettingsPanel.vue/LiveMatch.vue which stay English. This is deliberately the smallest
// thing that satisfies "es-ES default, English one tap away, a strings map is fine." es-ES
// register throughout (vocabulary/formats appropriate for Spain, per project convention) —
// "partner" doesn't come up anywhere in this content (no doubles/partner concept in this app),
// so the keep-untranslated rule doesn't apply.
import { reactive } from 'vue'

export type Locale = 'es' | 'en'

const state = reactive<{ locale: Locale }>({ locale: 'es' })

export function useLocale() {
  return state
}

export function toggleLocale() {
  state.locale = state.locale === 'es' ? 'en' : 'es'
}

export type StringKey =
  | 'back'
  | 'howToPlayTitle'
  | 'howToPlayStep1'
  | 'howToPlayStep2'
  | 'howToPlayStep3'
  | 'howToPlayStep4'
  | 'howToPlayStep5'
  | 'howToPlayStep6'
  | 'howToPlayStep7'
  | 'rulesTitle'
  | 'rulesSubtitle'
  | 'pickBothPlayersToStart'
  | 'fastModeLabel'
  | 'fastModeDescription'
  | 'fastConfirmTitle'
  | 'fastConfirmYes'
  | 'fastConfirmNo'
  | 'fastConfirmQuestion'

const STRINGS: Record<Locale, Record<StringKey, string>> = {
  es: {
    back: 'Volver',
    howToPlayTitle: 'Cómo jugar',
    howToPlayStep1:
      'Escanea tu credencial, o toca un lado de la mesa y elige tu nombre — la mesa mostrará a ambos jugadores en cuanto estéis los dos.',
    howToPlayStep2: 'Elige el modo de juego, el mejor de X y quién saca primero, y pulsa Empezar.',
    howToPlayStep3: 'Toca tu lado de la pantalla para sumar un punto cuando ganes el punto.',
    howToPlayStep4:
      'El anillo luminoso alrededor de la foto del jugador indica quién saca. El saque rota cada pocos puntos (2 en Rapid 11 por defecto, o el ajuste que elijas) — y en cada punto una vez llegados a deuce.',
    howToPlayStep5:
      'Los jugadores cambian de lado después de cada juego, y de nuevo a mitad del juego decisivo — un aviso en pantalla te lo indica.',
    howToPlayStep6: '¿Tocaste el lado equivocado? "Deshacer" quita el último punto anotado.',
    howToPlayStep7:
      'El marcador por voz (di "empezar", o tu marcador como "tres dos") llegará próximamente a este kiosco.',
    rulesTitle: 'Reglas oficiales de la ITTF',
    rulesSubtitle: 'Reglas de la ITTF, resumidas',
    // 2026-08-31 (Issue 2 rider) — Home's one-shot "Start match" needs both sides picked; this
    // is the disabled-state hint telling the player what's missing, es-ES first per project
    // convention, English via the same toggle everything else in this file uses.
    pickBothPlayersToStart: 'Elige los dos jugadores para empezar',
    // Round-31 (2026-08-31, DESIGN.md §23) — Settings toggle + LiveMatch confirm copy for
    // "Fast mode" (finalize a game's score directly instead of tracking every point).
    fastModeLabel: 'Modo rápido',
    fastModeDescription: 'Introduce el resultado final de cada juego en vez de cada punto — no queda historial punto a punto',
    fastConfirmTitle: 'Confirmar resultado',
    fastConfirmYes: 'Confirmar',
    fastConfirmNo: 'Cancelar',
    fastConfirmQuestion: '¿Terminar con',
  },
  en: {
    back: 'Back',
    howToPlayTitle: 'How to play',
    howToPlayStep1:
      'Scan your badge, or tap a side of the table and pick your name — the table shows both players once you\'re both in.',
    howToPlayStep2: 'Choose the game mode, best of X, and who serves first, then tap Start.',
    howToPlayStep3: 'Tap your side of the screen to score a point when you win the rally.',
    howToPlayStep4:
      "The glowing ring around a player's photo shows who's serving. The serve rotates every few points (2 for Rapid 11 by default, or your chosen setting) — and every single point once the game reaches deuce.",
    howToPlayStep5: 'Ends switch after every game, and again mid-way through the decider — an on-screen banner tells you when.',
    howToPlayStep6: "Tapped the wrong side? \"Undo\" takes back the last point scored.",
    howToPlayStep7: 'Voice score-calling (say "start", or call your score like "three two") is coming soon on this kiosk.',
    rulesTitle: 'Official ITTF rules',
    rulesSubtitle: 'ITTF rules, condensed',
    pickBothPlayersToStart: 'Pick both players to start',
    fastModeLabel: 'Fast mode',
    fastModeDescription: "Enter each game's final score instead of every point — no point-by-point history is kept",
    fastConfirmTitle: 'Confirm result',
    fastConfirmYes: 'Confirm',
    fastConfirmNo: 'Cancel',
    fastConfirmQuestion: 'End with',
  },
}

export function t(key: StringKey): string {
  return STRINGS[state.locale][key]
}

// Round-31 (2026-08-31) — the two fast-mode strings above need runtime numbers interpolated
// (final scores / match-type rules), which the static STRINGS map above doesn't support. Kept
// as small standalone functions rather than growing `t()` into a templating system for two
// call sites.
export function tFastConfirmBody(leftName: string, leftScore: number, rightName: string, rightScore: number): string {
  return `${t('fastConfirmQuestion')} ${leftScore}–${rightScore}? ${leftName} ${leftScore} · ${rightName} ${rightScore}`
}

export function tFastInvalidScore(pointsToWin: number, winBy: number): string {
  return state.locale === 'es'
    ? `Marcador no válido: hay que llegar a ${pointsToWin} puntos ganando por ${winBy} o más`
    : `Invalid score: must reach ${pointsToWin} points, winning by ${winBy} or more`
}
